import {
  completeCalcParams,
  remapLegacySarParams,
  type IndicatorStyleOverrides,
} from '@edge/charts-indicators'
import type { TimeframeVisibilityRule } from './timeframeVisibility'
import type { IndicatorEntry } from '../types/persistence'
import { createLocalStore } from './keyedLocalStore'

/**
 * Per-user default for a single indicator, persisted to localStorage so it
 * survives across charts and sessions. When the user adds a fresh indicator
 * from the picker, the catalog defaults are layered with whatever lives here.
 *
 * Intentionally local-only — no network round-trip, no backend schema
 * commitment. If we ever want server-side sync, the read/write surface
 * defined here is the seam to swap out.
 */
export interface IndicatorUserDefault {
  calcParams?: unknown[]
  styles?: IndicatorStyleOverrides
  /** Default per-timeframe visibility rule applied to freshly-added instances. */
  timeframeVisibility?: TimeframeVisibilityRule
}

const store = createLocalStore<IndicatorUserDefault>('edge.chart.indicatorDefaults.v1')

/**
 * Change listeners — the seam the app-level server-sync hook plugs into.
 * Writes happen deep inside charts-core (the drawer's "Save as my default"),
 * far from any React state, so without this the host app has no signal that
 * the map changed and nothing to push to the user record.
 */
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((l) => l())
}

/** Subscribe to any change of the defaults map. Returns an unsubscribe fn. */
export function subscribeIndicatorUserDefaults(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The saved default for `name`. A SAR default saved before the slot-order fix comes
 * back reordered (see remapLegacySarParams). The stored map stays as saved: the
 * server sync pushes `getAllIndicatorUserDefaults` as is, so a read never writes.
 */
export function getIndicatorUserDefault(name: string): IndicatorUserDefault | undefined {
  const saved = store.get(name)
  const calcParams = remapLegacySarParams(name, saved?.calcParams)
  return saved && calcParams !== saved.calcParams ? { ...saved, calcParams } : saved
}

export function setIndicatorUserDefault(name: string, value: IndicatorUserDefault): void {
  store.set(name, value)
  notify()
}

export function clearIndicatorUserDefault(name: string): void {
  store.clear(name)
  notify()
}

/** The whole map — what the server-sync hook pushes to the user record. */
export function getAllIndicatorUserDefaults(): Record<string, IndicatorUserDefault> {
  return store.readAll()
}

/**
 * Overwrite the whole map — the server-wins path on first profile load.
 * Notifies subscribers; the sync hook dedupes via JSON compare so applying
 * the server's own value never echoes a write back up.
 */
export function replaceAllIndicatorUserDefaults(next: Record<string, IndicatorUserDefault>): void {
  store.replaceAll(next)
  notify()
}

/** What an add path should actually mount: user default layered over picker catalog values. */
export interface LayeredIndicatorAdd {
  calcParams?: unknown[]
  styles?: IndicatorStyleOverrides
  timeframeVisibility?: TimeframeVisibilityRule
}

/**
 * Layer the user's saved default for `name` over the calcParams a picker
 * supplies. This is the single seam EVERY indicator-add path must go through —
 * EdgeChart's embedded toolbar and the workspace panel-header picker both use
 * it. The workspace path once skipped it and built entries straight from the
 * catalog, which made "Save current as Default" a silent no-op on all
 * workspace charts (found via BOLL, 2026-08-01).
 */
export function layerIndicatorUserDefault(
  name: string,
  calcParams?: unknown[],
): LayeredIndicatorAdd {
  const d = getIndicatorUserDefault(name)
  return {
    // A default saved while the drawer emitted partial arrays would mount blank.
    calcParams: completeCalcParams(name, d?.calcParams ?? calcParams),
    styles: d?.styles,
    timeframeVisibility: d?.timeframeVisibility,
  }
}

/**
 * Build the `IndicatorEntry` an add path should mount: the user's saved default
 * ("Save current as Default" in the indicator drawer) layered over the picker's
 * catalog params, collapsed back to a bare name when there is nothing to carry.
 *
 * Every indicator-add path calls THIS, not `layerIndicatorUserDefault` plus its
 * own entry-building. The layering step and the entry shape were previously
 * copied per call site, and four of the five chart grid shells had neither, so
 * "Save current as Default" was a silent no-op on the dashboard multi-chart,
 * History's Chart Scanner and the Backtester's Build chart (the same class of
 * bug as the BOLL one fixed here 2026-08-01, which reached only one shell).
 * `chart-grids-use-indicator-defaults.unit.test.ts` in the frontend keeps new
 * add paths on this seam.
 */
export function indicatorEntryWithUserDefault(
  name: string,
  calcParams?: unknown[],
): IndicatorEntry {
  const layered = layerIndicatorUserDefault(name, calcParams)
  if (!layered.calcParams && !layered.styles && !layered.timeframeVisibility) return name
  return {
    name,
    ...(layered.calcParams ? { calcParams: layered.calcParams } : {}),
    ...(layered.styles ? { styles: layered.styles } : {}),
    ...(layered.timeframeVisibility ? { timeframeVisibility: layered.timeframeVisibility } : {}),
  }
}
